import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { scheduledPaymentInfoNoteSx } from '../../pages/dashboard/DashboardStyles';
import { formatCurrency } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import { AutopayInfoWrapper } from '../global-styles/GlobalStyles';
import { colors } from '../theme/theme';

import type { PaymentMethodType, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { SxProps, Theme } from '@mui/material/styles';

export interface CardFeeByRowEntry {
    fee: number;
    charge: number;
    isDisabled: boolean;
    isMatched: boolean;
    infoMessage?: string;
    isLoading?: boolean;
}

interface PaymentRowNoticesProps {
    row: PaymentRow;
    contract: ContractApiResponse | null;
    paymentMethodType: PaymentMethodType;
    cardFeeEntry?: CardFeeByRowEntry;
    submitError?: string | null;
    /** When provided, wraps rendered notices in a Box with this sx (skipped when there is nothing to show). */
    wrapperSx?: SxProps<Theme>;
}

/** Submit-error / disabled-reason / scheduled-payment / commercial-contract notices for a payment row — shared by the desktop table's row-detail and the mobile contract accordion. */
export const PaymentRowNotices: React.FC<PaymentRowNoticesProps> = ({
    row,
    contract,
    paymentMethodType,
    cardFeeEntry,
    submitError,
    wrapperSx,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const scheduledAmount = parseFloat(String(row.scheduledAmount ?? '0')) || 0;
    const outstandingBalance =
        contract?.OutstandingBalance != null
            ? Number(contract.OutstandingBalance)
            : contract?.RemainingPrincipal != null
              ? Number(contract.RemainingPrincipal)
              : null;

    const isDisabledDuplicate = paymentMethodType === 'card' && cardFeeEntry?.isDisabled && cardFeeEntry?.infoMessage;

    const isRowDisabled = cardFeeEntry?.isDisabled || cardFeeEntry?.isLoading;
    const isBalanceCovered =
        outstandingBalance != null && scheduledAmount >= outstandingBalance && outstandingBalance > 0;

    let disabledReason = '';
    if (isBalanceCovered) {
        disabledReason = "This contract's balance is already fully covered by open invoices and scheduled payments.";
    } else if (isRowDisabled && !cardFeeEntry?.infoMessage) {
        if (contract?.AutoPay) {
            disabledReason = 'AutoPay is enabled. Disable AutoPay to make one-time payments.';
        } else if (paymentMethodType === 'card' && (contract?.AccountType !== 'I' || !contract?.IsLoan)) {
            disabledReason = 'Card payments are only available for Consumer Loan contracts.';
        } else {
            disabledReason = 'This contract is not eligible for payment with the selected payment method.';
        }
    }

    const isCommercialContract = contract?.AccountType === 'B';

    if (scheduledAmount <= 0 && !isDisabledDuplicate && !submitError && !disabledReason && !isCommercialContract) {
        return null;
    }

    const content = (
        <>
            {submitError && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        p: '12px 16px',
                        bgcolor: colors.errorPreset[20],
                        borderLeft: `3px solid ${colors.errorPreset[10]}`,
                        mb: scheduledAmount > 0 || isDisabledDuplicate || disabledReason ? 2 : 0,
                    }}
                >
                    <Typography variant="body2" color="error.main">
                        {submitError}
                    </Typography>
                </Stack>
            )}
            {disabledReason && !isDisabledDuplicate && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        p: '12px 16px',
                        bgcolor: colors.warningPreset[80],
                        borderLeft: `3px solid ${colors.warningPreset[50]}`,
                        mb: scheduledAmount > 0 ? 2 : 0,
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#6B4F00' }}>
                        {disabledReason}
                    </Typography>
                </Stack>
            )}
            {isDisabledDuplicate && scheduledAmount <= 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        p: '12px 16px',
                        bgcolor: colors.bluePreset[50],
                        borderLeft: `3px solid ${colors.bluePreset[90]}`,
                    }}
                >
                    <Typography variant="body2" color="info.main">
                        {cardFeeEntry?.infoMessage}
                    </Typography>
                </Stack>
            )}
            {scheduledAmount > 0 && (
                <Stack direction="row" alignItems="center" sx={scheduledPaymentInfoNoteSx}>
                    <span className="icon-info-filled" style={{ fontSize: 24 }} />
                    <Typography variant="caption" component="span">
                        {(() => {
                            const amountStr = formatCurrency(scheduledAmount);
                            const template = t('makePayment.scheduledPaymentInfoNote');
                            const parts = template.split('{{amount}}');
                            if (parts.length < 2) return template.replace('{{amount}}', amountStr);
                            return (
                                <>
                                    {parts[0]}
                                    <Box component="span" fontWeight={600}>
                                        {amountStr}
                                    </Box>
                                    {parts[1]}{' '}
                                    <Box
                                        component="a"
                                        href={NavigationConfig.Payments}
                                        onClick={(e: React.MouseEvent) => {
                                            e.preventDefault();
                                            navigate(NavigationConfig.Payments, {
                                                state: {
                                                    activeTab: 1,
                                                    contractFilter: row.contractId,
                                                },
                                            });
                                        }}
                                        sx={{
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            pointerEvents: 'auto !important',
                                            position: 'relative',
                                            zIndex: 10,
                                        }}
                                    >
                                        {t('makePayment.scheduledPaymentCheckLink')}
                                    </Box>
                                </>
                            );
                        })()}
                    </Typography>
                </Stack>
            )}
            {isCommercialContract && (
                <AutopayInfoWrapper direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <span className="icon-info-filled" />
                    <Typography variant="subtitle2">{t('makePayment.CommercialContractsNote')}</Typography>
                </AutopayInfoWrapper>
            )}
        </>
    );

    return wrapperSx ? <Box sx={wrapperSx}>{content}</Box> : content;
};
